// ── ApprovalCard (Evo Brain — Approval Card Pattern) ─────────────────────────
// DESIGN_SYSTEM.md § Approval Card Pattern. Compone Card + Pill + Button +
// IconButton + Icon — cero primitives nuevos, cero tokens nuevos.
//
// Deviación reportada del props reference documentado (ver reporte del paso 4):
// `sentence` llega como string ya renderizado (el backend arma la oración en
// español leyendo el estado real de la DB — describeProposal), no como
// {key,params}: templetear de nuevo en el cliente sería envolver un string ya
// final sin ganancia. `costUsd` se reemplaza por `warning` (texto más rico que
// ya produce el backend) en el mismo slot visual (fs-caption, fg-3, bolt).
// Relative age usa Intl.RelativeTimeFormat (cero dependencia nueva) en vez de
// date-fns, que no está declarado como dependencia real de packages/web.
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from './Card.js';
import { Pill, type PillTone } from '../PillGroup.js';
import { Button } from './Button.js';
import { IconButton } from './IconButton.js';
import { Icon } from '../Icon.js';
import { Checkbox } from './Checkbox.js';
import { parseServerDate } from '../../lib/date.js';

export type ProposalStatus = 'pending' | 'approved' | 'rejected';
export type ProposalVerb = 'create' | 'edit' | 'send' | 'money' | 'delete' | 'publish';

export interface ProposalChange {
  field: string;
  from: string | null;
  to: string;
}

export interface ApprovalProposalItem {
  id: number;              // proposal message id — React key
  taskId: string;
  turnGroupId: string;
  toolName: string;
  verb: ProposalVerb;
  sentence: string;
  changes?: ProposalChange[] | undefined;
  irreversible?: boolean | undefined;
  warning?: string | undefined;
  createdAt: string;
  status: ProposalStatus;
}

export interface ApprovalCardEntity {
  label: string;
  href: string;
}

export interface ApprovalCardProps {
  proposals: ApprovalProposalItem[];
  entity?: ApprovalCardEntity | null | undefined;
  onApprove: (taskId: string, remember?: boolean) => void;
  onReject: (taskId: string) => void;
  onApproveAll?: (() => void) | undefined;
  onRejectAll?: (() => void) | undefined;
  approvingId?: string | null | undefined;
  rejectingId?: string | null | undefined;
  approvingAll?: boolean | undefined;
  rejectingAll?: boolean | undefined;
}

const VERB_TONE: Record<ProposalVerb, PillTone> = {
  create: 'cyan',
  edit: 'cyan',
  publish: 'cyan',
  send: 'blue',
  delete: 'danger',
  money: 'amber',
};

const STATUS_BORDER: Record<ProposalStatus, string> = {
  pending: 'var(--evo-cyan)',
  approved: 'var(--evo-stroke)',
  rejected: 'var(--evo-stroke)',
};

const STATUS_TONE: Record<Exclude<ProposalStatus, 'pending'>, PillTone> = {
  approved: 'success',
  rejected: 'muted',
};

// ── Responsive: la fila ACTUAL→NUEVO reflow a stack vertical en mobile ───────
// Mismo patrón que KVGrid.tsx — matchMedia + estado, sin CSS media queries
// inline (no soportadas por style objects).
function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 639px)').matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return narrow;
}

// parseServerDate movido a lib/date.ts (compartido — ver ese archivo para el
// detalle del bug). Se usaba solo acá; ahora también en TasksPage,
// TaskDetailPage y todo lo demás que renderiza un timestamp TEXT naive-UTC.

// ── Edad relativa localizada (Intl.RelativeTimeFormat — cero deps) ──────────
function useRelativeAge(iso: string): string {
  const { i18n } = useTranslation();
  const diffMs = Date.now() - parseServerDate(iso).getTime();
  const rtf = new Intl.RelativeTimeFormat(i18n.language.startsWith('es') ? 'es' : 'en', { numeric: 'auto' });
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return rtf.format(-sec, 'second');
  const min = Math.round(sec / 60);
  if (min < 60) return rtf.format(-min, 'minute');
  const hr = Math.round(min / 60);
  if (hr < 24) return rtf.format(-hr, 'hour');
  const day = Math.round(hr / 24);
  return rtf.format(-day, 'day');
}

// ── Fila de comparación ACTUAL → NUEVO ───────────────────────────────────────

function CompareRow({ change }: { change: ProposalChange }) {
  const { t } = useTranslation();
  const narrow = useNarrow();

  if (change.from == null) {
    // Acción de creación — sin ACTUAL, no se inventa un "—"
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', textTransform: 'uppercase', letterSpacing: 'var(--evo-ls-caps)' }}>
          {t('evobrain.card.new', { defaultValue: 'Nuevo' })}
        </span>
        <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-1)', fontWeight: 'var(--evo-fw-medium)' }}>{change.to}</span>
      </div>
    );
  }

  if (narrow) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', textTransform: 'uppercase', letterSpacing: 'var(--evo-ls-caps)' }}>
            {t('evobrain.card.current', { defaultValue: 'Actual' })}
          </span>
          <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-3)' }}>{change.from}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', textTransform: 'uppercase', letterSpacing: 'var(--evo-ls-caps)' }}>
            {t('evobrain.card.new', { defaultValue: 'Nuevo' })}
          </span>
          <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-1)', fontWeight: 'var(--evo-fw-medium)' }}>{change.to}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
      <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{change.from}</span>
      <span style={{ display: 'inline-flex', color: 'var(--evo-fg-4)', flexShrink: 0 }}><Icon name="arrow-right" size={12} /></span>
      <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-1)', fontWeight: 'var(--evo-fw-medium)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{change.to}</span>
    </div>
  );
}

// ── Meta row: pill Irreversible + nota de costo ──────────────────────────────

function MetaRow({ irreversible, warning }: { irreversible?: boolean | undefined; warning?: string | undefined }) {
  const { t } = useTranslation();
  if (!irreversible && !warning) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexWrap: 'wrap' }}>
      {irreversible && (
        <Pill size="xs" variant="subtle" tone="warn">{t('evobrain.card.irreversible', { defaultValue: 'Irreversible' })}</Pill>
      )}
      {warning && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
          <Icon name="bolt" size={10} />
          {warning}
        </span>
      )}
    </div>
  );
}

// ── Checkbox "no preguntar más" — solo para propuestas reversibles ──────────
// Nunca se ofrece en propuestas irreversible:true (envíos, y cualquier otra
// acción marcada así) — condición explícita de Eduardo, sin excepción. El
// backend también la re-chequea (grantPermanentApproval se auto-rechaza) —
// esto es la primera red, no la única.

function RememberRow({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
      <Checkbox size="sm" checked={checked} onChange={onChange} />
      <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
        {t('evobrain.card.rememberApproval', { defaultValue: 'No preguntar más para esta acción' })}
      </span>
    </div>
  );
}

// ── Individual (una sola propuesta) ──────────────────────────────────────────

function SingleCard({
  proposal, entity, onApprove, onReject, approving, rejecting,
}: {
  proposal: ApprovalProposalItem;
  entity?: ApprovalCardEntity | null | undefined;
  onApprove: (remember: boolean) => void;
  onReject: () => void;
  approving: boolean;
  rejecting: boolean;
}) {
  const { t } = useTranslation();
  const age = useRelativeAge(proposal.createdAt);
  const isPending = proposal.status === 'pending';
  const [remember, setRemember] = useState(false);

  return (
    <Card
      padding="md"
      style={{ border: `1px solid ${STATUS_BORDER[proposal.status]}`, transition: `border-color var(--evo-motion-collapse)` }}
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', width: '100%' }}>
          <Pill size="sm" variant="subtle" tone={VERB_TONE[proposal.verb]}>
            {t(`evobrain.verb.${proposal.verb}`, { defaultValue: proposal.verb })}
          </Pill>
          {entity && (
            <a href={entity.href} style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-2)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
              {entity.label}
            </a>
          )}
          <span style={{ flex: 1 }} />
          {!isPending && (
            <Pill size="xs" variant="subtle" tone={STATUS_TONE[proposal.status as Exclude<ProposalStatus, 'pending'>]}>
              {t(`evobrain.card.status.${proposal.status}`, { defaultValue: proposal.status })}
            </Pill>
          )}
          <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', flexShrink: 0 }}>{age}</span>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)', opacity: proposal.status === 'rejected' ? 0.5 : 1 }}>
        <p style={{ margin: 0, fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-1)', lineHeight: 1.5 }}>{proposal.sentence}</p>
        {proposal.changes && proposal.changes.length > 0 && <CompareRow change={proposal.changes[0]!} />}
        <MetaRow irreversible={proposal.irreversible} warning={proposal.warning} />
      </div>

      {isPending && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)', marginTop: 'var(--evo-space-4)', paddingTop: 'var(--evo-space-3)', borderTop: '1px solid var(--evo-stroke)' }}>
          {!proposal.irreversible && <RememberRow checked={remember} onChange={setRemember} />}
          <div style={{ display: 'flex', gap: 'var(--evo-space-2)' }}>
            <Button variant="solid" tone="cyan" size="sm" loading={approving} disabled={rejecting} onClick={() => onApprove(remember)}>
              {t('evobrain.card.approve', { defaultValue: 'Aprobar' })}
            </Button>
            <Button variant="outline" tone="muted" size="sm" loading={rejecting} disabled={approving} onClick={onReject}>
              {t('evobrain.card.reject', { defaultValue: 'Rechazar' })}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Agrupada (varias propuestas del mismo turno) ─────────────────────────────

function GroupedCard({
  proposals, entity, onApprove, onReject, onApproveAll, onRejectAll,
  approvingId, rejectingId, approvingAll, rejectingAll,
}: ApprovalCardProps) {
  const { t } = useTranslation();
  const newestAge = useRelativeAge(proposals[0]!.createdAt);
  const pending = proposals.filter(p => p.status === 'pending');
  const [rememberByTaskId, setRememberByTaskId] = useState<Record<string, boolean>>({});
  // "Aprobar todas" solo si TODAS las pendientes son reversibles (regla del pattern).
  const canBulkApprove = pending.length > 1 && pending.every(p => !p.irreversible);

  return (
    <Card
      padding="none"
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', width: '100%' }}>
          <Pill size="sm" variant="subtle" tone="default">
            {t('evobrain.card.actionsCount', { count: proposals.length, defaultValue: `${proposals.length} acciones` })}
          </Pill>
          {entity && (
            <a href={entity.href} style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-2)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
              {entity.label}
            </a>
          )}
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', flexShrink: 0 }}>{newestAge}</span>
        </div>
      }
      style={{ border: `1px solid ${pending.length > 0 ? 'var(--evo-cyan)' : 'var(--evo-stroke)'}`, transition: 'border-color var(--evo-motion-collapse)' }}
    >
      <div>
        {proposals.map((p, idx) => (
          <div key={p.id}>
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-3)',
              padding: 'var(--evo-space-3) var(--evo-space-4)',
              opacity: p.status === 'rejected' ? 0.5 : 1,
            }}>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
                <p style={{ margin: 0, fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-1)', lineHeight: 1.45 }}>{p.sentence}</p>
                {p.changes && p.changes.length > 0 && <CompareRow change={p.changes[0]!} />}
                <MetaRow irreversible={p.irreversible} warning={p.warning} />
                {p.status === 'pending' && !p.irreversible && (
                  <RememberRow
                    checked={rememberByTaskId[p.taskId] ?? false}
                    onChange={v => setRememberByTaskId(prev => ({ ...prev, [p.taskId]: v }))}
                  />
                )}
              </div>
              {p.status === 'pending' ? (
                <div style={{ display: 'flex', gap: 'var(--evo-space-1)', flexShrink: 0 }}>
                  <IconButton
                    icon={<Icon name="ok" size={14} />}
                    ariaLabel={t('evobrain.card.approve', { defaultValue: 'Aprobar' })}
                    tone="cyan"
                    variant="outline"
                    size="md"
                    disabled={approvingId != null || rejectingId != null}
                    onClick={() => onApprove(p.taskId, rememberByTaskId[p.taskId] ?? false)}
                  />
                  <IconButton
                    icon={<Icon name="close" size={14} />}
                    ariaLabel={t('evobrain.card.reject', { defaultValue: 'Rechazar' })}
                    tone="muted"
                    variant="outline"
                    size="md"
                    disabled={approvingId != null || rejectingId != null}
                    onClick={() => onReject(p.taskId)}
                  />
                </div>
              ) : (
                <Pill size="xs" variant="subtle" tone={STATUS_TONE[p.status as Exclude<ProposalStatus, 'pending'>]}>
                  {t(`evobrain.card.status.${p.status}`, { defaultValue: p.status })}
                </Pill>
              )}
            </div>
            {idx < proposals.length - 1 && (
              <div style={{ margin: '0 var(--evo-space-4)', height: '1px', background: 'var(--evo-stroke)' }} />
            )}
          </div>
        ))}
      </div>

      {pending.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--evo-space-2)', padding: 'var(--evo-space-3) var(--evo-space-4)', borderTop: '1px solid var(--evo-stroke)' }}>
          {canBulkApprove && (
            <Button variant="solid" tone="cyan" size="sm" loading={!!approvingAll} disabled={!!rejectingAll} onClick={() => onApproveAll?.()}>
              {t('evobrain.card.approveAll', { defaultValue: 'Aprobar todas' })}
            </Button>
          )}
          <Button variant="outline" tone="muted" size="sm" loading={!!rejectingAll} disabled={!!approvingAll} onClick={() => onRejectAll?.()}>
            {t('evobrain.card.rejectAll', { defaultValue: 'Rechazar todas' })}
          </Button>
        </div>
      )}
    </Card>
  );
}

// ── Entry point ───────────────────────────────────────────────────────────────

export function ApprovalCard(props: ApprovalCardProps): ReactNode {
  const { proposals, entity, onApprove, onReject, approvingId, rejectingId } = props;
  if (proposals.length === 0) return null;

  if (proposals.length === 1) {
    const p = proposals[0]!;
    return (
      <SingleCard
        proposal={p}
        entity={entity}
        onApprove={remember => onApprove(p.taskId, remember)}
        onReject={() => onReject(p.taskId)}
        approving={approvingId === p.taskId}
        rejecting={rejectingId === p.taskId}
      />
    );
  }

  return <GroupedCard {...props} />;
}
